/**
 * Media cache
 *
 * Chat images and voice notes are fetched with a bearer token and shown
 * through object URLs, which means switching conversations used to refetch
 * every picture and re-decode every clip. This keeps one object URL per file
 * id for the session, so a bubble that has been seen once renders at once.
 *
 * A file id names an immutable blob, so an entry can never go stale — it is
 * only ever evicted for space (oldest first) or dropped on logout. Eviction
 * revokes the URL, which is why callers must not revoke what they get here.
 */

const MAX_ENTRIES = 200;

const urls = new Map(); // fileId -> object URL, in last-used order
const loading = new Map(); // fileId -> Promise<object URL>

const touch = (fileId, url) => {
  // Re-inserting moves the entry to the end, which is what makes the first
  // key the least recently used one.
  urls.delete(fileId);
  urls.set(fileId, url);
};

/** The object URL for a file already fetched this session, else null. */
export const getCachedMediaUrl = (fileId) => {
  if (!fileId) return null;
  const url = urls.get(fileId);
  if (!url) return null;
  touch(fileId, url);
  return url;
};

/**
 * Resolve an object URL for a file, fetching it through `fetchBlob` at most
 * once even if several bubbles ask at the same time.
 */
export const loadMediaUrl = (fileId, fetchBlob) => {
  const cached = getCachedMediaUrl(fileId);
  if (cached) return Promise.resolve(cached);

  const pending = loading.get(fileId);
  if (pending) return pending;

  const request = Promise.resolve()
    .then(fetchBlob)
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      if (urls.size >= MAX_ENTRIES) {
        const oldest = urls.keys().next().value;
        URL.revokeObjectURL(urls.get(oldest));
        urls.delete(oldest);
      }
      touch(fileId, url);
      return url;
    })
    .finally(() => {
      loading.delete(fileId);
    });

  loading.set(fileId, request);
  return request;
};

/** Release every object URL. Called on logout. */
export const clearMediaCache = () => {
  urls.forEach((url) => URL.revokeObjectURL(url));
  urls.clear();
  loading.clear();
};
